import type { AxiosError } from 'axios';
import { useContext, useEffect, useState } from 'react';
import type { FieldValues } from 'react-hook-form';

import { type FieldEditorProps, FieldTypes } from '@/components/PersonInPlaceEditor/types';
import AppContext from '@/contexts/appContext';
import type { OptionType } from '@/ui/components/Form/Select/types';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';

function useFetchFormFieldOptions<T extends FieldValues>({
  field,
  value
}: Pick<FieldEditorProps<T>, 'field' | 'value'>) {
  const hasOptions = field.type === FieldTypes.SELECT || field.type === FieldTypes.RADIO;
  const receivedOptions = 'options' in field && Array.isArray(field.options) ? field.options : null;
  const fetchOptions =
    'options' in field && typeof field.options === 'function' ? field.options : null;

  const { addMessage } = useContext(AppContext);

  const [options, setOptions] = useState<OptionType[]>(() => {
    return hasOptions && receivedOptions
      ? receivedOptions
      : !hasOptions
        ? []
        : [{ label: typeof field.label === 'string' ? field.label : '', value }];
  });
  const [loadingOptions, setLoadingOptions] = useState<boolean>(false);

  useEffect(() => {
    const loadOptions = async () => {
      if (!hasOptions || receivedOptions) return;

      if (fetchOptions) {
        try {
          setLoadingOptions(true);
          const fetchedOptions = await fetchOptions();
          setOptions(fetchedOptions);
        } catch (error) {
          addMessage(mapAxiosErrorMessages(error as AxiosError));
        } finally {
          setLoadingOptions(false);
        }
      }
    };

    loadOptions();
  }, [addMessage, fetchOptions, hasOptions, receivedOptions]);

  return { options, loadingOptions };
}

export default useFetchFormFieldOptions;
